import { useEffect } from 'react';
import { VideoEditorLogger, VideoEditorLogChannel } from '@src/utility/VideoEditorLogger';
import { LogLevel, LogManager } from '@src/utility/Logger';

/**
 * Configuration options for VideoEditor logging
 */
export interface VideoEditorLoggingOptions {
  /** Enable command logging */
  enableCommandLogging?: boolean;
  /** Enable timeline operation logging */
  enableTimelineLogging?: boolean;
  /** Enable transcript operation logging */
  enableTranscriptLogging?: boolean;
  /** Enable batch operation logging */
  enableBatchLogging?: boolean;
  /** Enable performance measurement logging */
  enablePerformanceLogging?: boolean;
  /** Log level for commands */
  commandLogLevel?: LogLevel;
  /** Detailed command performance measurement */
  detailedCommandPerformance?: boolean;
}

/**
 * Hook that configures VideoEditor logging
 * 
 * This hook should be used in components that use the VideoEditor
 * to configure logging specifically for editor operations.
 * 
 * @param editor The editor instance to configure logging for
 * @param options VideoEditor logging configuration options
 * @param enabled Master switch to enable/disable all VideoEditor logging
 */
export function useVideoEditorLogging(
  editor: any,
  options: VideoEditorLoggingOptions = {},
  enabled: boolean = process.env.NODE_ENV === 'development'
) {
  useEffect(() => {
    if (!editor || !enabled) return;
    
    // Only run once the editor is available
    const isDevEnv = process.env.NODE_ENV === 'development';
    const isTestEnv = process.env.NODE_ENV === 'test';
    
    // Initialize editor logging with specified options
    editor.initializeLogging({
      enableCommandLogging: options.enableCommandLogging ?? (isDevEnv || isTestEnv),
      enableTimelineLogging: options.enableTimelineLogging ?? isDevEnv,
      enableTranscriptLogging: options.enableTranscriptLogging ?? isDevEnv,
      enableBatchLogging: options.enableBatchLogging ?? isDevEnv,
      enablePerformanceLogging: options.enablePerformanceLogging ?? isDevEnv,
    });
    
    // Set command log level if specified
    if (options.commandLogLevel !== undefined) {
      LogManager.setChannelLevel(
        VideoEditorLogChannel.COMMANDS, 
        options.commandLogLevel
      );
    }
    
    // Configure detailed performance logging if requested
    if (options.detailedCommandPerformance) {
      LogManager.setChannelLevel(
        VideoEditorLogChannel.PERFORMANCE,
        LogLevel.DEBUG
      );
    }
    
    VideoEditorLogger.logStateChange('editor_logging_initialized', {
      enabledChannels: {
        commands: options.enableCommandLogging ?? isDevEnv,
        timeline: options.enableTimelineLogging ?? isDevEnv,
        transcript: options.enableTranscriptLogging ?? isDevEnv,
        batching: options.enableBatchLogging ?? isDevEnv,
        performance: options.enablePerformanceLogging ?? isDevEnv,
      }
    });
    
    return () => {
      // No cleanup needed
    };
  }, [
    editor,
    enabled,
    options.enableCommandLogging,
    options.enableTimelineLogging,
    options.enableTranscriptLogging,
    options.enableBatchLogging,
    options.enablePerformanceLogging,
    options.commandLogLevel,
    options.detailedCommandPerformance,
  ]);
}